import React from 'react';
import {
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentArrowDownIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  CalculatorIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

export default function AnalyticsHelp() {
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-green-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-emerald-600 p-3 rounded-full">
              <ChartBarIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Time Tracking Analytics</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive guide to workforce analytics, payroll processing, and time management
            with advanced reporting capabilities and Sage-compatible exports.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <CalculatorIcon className="h-6 w-6 text-emerald-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The Time Tracking Analytics system provides comprehensive workforce management capabilities,
            combining time tracking, payroll calculations, absence management, and advanced reporting.
            Generate accurate payroll data, track productivity metrics, and export Sage-compatible
            files for seamless payroll processing.
          </p>
        </div>

        {/* Core Features */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-emerald-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Core Analytics Features</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <ClockIcon className="h-6 w-6 text-emerald-600 mr-3" />
                <h3 className="text-xl font-semibold text-emerald-800">Time Tracking</h3>
              </div>
              <p className="text-emerald-700 mb-4">Comprehensive clock-in/out data with break management</p>
              <ul className="space-y-2 text-emerald-600">
                <li>• Scheduled vs actual times</li>
                <li>• Break start/end tracking</li>
                <li>• Automatic break deductions</li>
                <li>• Late arrival monitoring</li>
              </ul>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-blue-800">Payroll Calculations</h3>
              </div>
              <p className="text-blue-700 mb-4">Accurate pay calculations with multiple time sources</p>
              <ul className="space-y-2 text-blue-600">
                <li>• Pre-break and post-break pay</li>
                <li>• Hourly rate calculations</li>
                <li>• End time source selection</li>
                <li>• Long shift break policies</li>
              </ul>
            </div>

            <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <DocumentArrowDownIcon className="h-6 w-6 text-purple-600 mr-3" />
                <h3 className="text-xl font-semibold text-purple-800">Sage Integration</h3>
              </div>
              <p className="text-purple-700 mb-4">Seamless export to Sage payroll systems</p>
              <ul className="space-y-2 text-purple-600">
                <li>• Compatible CSV format</li>
                <li>• Multiple pay codes</li>
                <li>• SSP and holiday integration</li>
                <li>• Automated data mapping</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Data Filtering */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <CalendarDaysIcon className="h-6 w-6 text-emerald-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Advanced Filtering</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Date Range Selection:</strong> Filter data by custom start and end dates for precise reporting periods</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Staff Member Filtering:</strong> View data for individual employees or all staff members</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Real-time Updates:</strong> Data refreshes automatically when new clock-ins are recorded</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Comprehensive Coverage:</strong> Includes worked shifts, sick days, and holiday periods</span>
              </li>
            </ul>
          </div>

          {/* Time Calculations */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <CalculatorIcon className="h-6 w-6 text-emerald-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Intelligent Calculations</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Flexible End Times:</strong> Choose between scheduled end times or actual clock-out times</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Break Deductions:</strong> Automatic unpaid break calculations for shifts 6+ hours</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Pre/Post Break Pay:</strong> Separate calculations showing pay before and after break deductions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Accuracy Validation:</strong> Visual indicators for break application and time source selection</span>
              </li>
            </ul>
          </div>

          {/* Absence Management */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="h-6 w-6 text-emerald-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Absence Analytics</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>SSP Tracking:</strong> Automatic Statutory Sick Pay calculations for eligible absences</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Holiday Pay:</strong> Contracted hours-based holiday pay calculations</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Consecutive Sick Days:</strong> SSP eligibility based on consecutive absence patterns</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Half-Day Holidays:</strong> Support for half-day holiday bookings and calculations</span>
              </li>
            </ul>
          </div>

          {/* Export Capabilities */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <TableCellsIcon className="h-6 w-6 text-emerald-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Export & Integration</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Sage Compatible CSV:</strong> One-click export in Sage payroll system format</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Multiple Pay Codes:</strong> NORMAL, SSP, and HOLIDAY pay code categorization</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Structured Data:</strong> Employee reference, date, units, rate, amount, and narrative fields</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Payroll Ready:</strong> Direct import capability into Sage accounting systems</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Data Tables Overview */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-xl p-8 mb-8 text-white">
          <div className="flex items-center mb-6">
            <TableCellsIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">Data Tables & Reporting</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <h3 className="font-semibold mb-2">Clock-In Data</h3>
              <p className="text-emerald-100 text-sm">Detailed shift information with time tracking and break management</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <h3 className="font-semibold mb-2">Staff Summary</h3>
              <p className="text-emerald-100 text-sm">Aggregated hours and pay totals per employee</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <h3 className="font-semibold mb-2">Sick Days</h3>
              <p className="text-emerald-100 text-sm">SSP eligibility tracking and consecutive absence monitoring</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">4</span>
              </div>
              <h3 className="font-semibold mb-2">Holiday Pay</h3>
              <p className="text-emerald-100 text-sm">Contracted hours-based holiday pay calculations</p>
            </div>
          </div>
        </div>

        {/* Time Source Selection */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Understanding Time Calculations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 End Time Sources</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Shift End:</strong> Uses the scheduled end time from the shift roster</li>
                <li><strong>Clock Out:</strong> Uses the actual time the employee clocked out</li>
                <li><strong>Flexible Selection:</strong> Choose the most appropriate time source per shift</li>
                <li><strong>Visual Indicators:</strong> Clear display of which time source is being used</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⏰ Break Calculations</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>6+ Hour Rule:</strong> Unpaid breaks applied only to shifts longer than 6 hours</li>
                <li><strong>Pre-Break Pay:</strong> Shows pay calculation before break deduction</li>
                <li><strong>Post-Break Pay:</strong> Final pay after unpaid break time is removed</li>
                <li><strong>Break Duration:</strong> Configurable unpaid break minutes per employee</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">💰 SSP Calculations</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Eligibility:</strong> SSP paid for sick days beyond the 3-day waiting period</li>
                <li><strong>Consecutive Days:</strong> Tracks consecutive sick days for SSP qualification</li>
                <li><strong>Standard Rate:</strong> £23.75 per day SSP (2025 rate)</li>
                <li><strong>Integration:</strong> Automatically included in Sage export files</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🏖️ Holiday Pay</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Contracted Hours:</strong> Based on employee's contracted weekly hours</li>
                <li><strong>Daily Rate:</strong> Calculated as weekly hours divided by contracted days</li>
                <li><strong>Half Days:</strong> Support for half-day holiday bookings</li>
                <li><strong>Pay Integration:</strong> Full holiday pay included in payroll exports</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Analytics Best Practices & Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📅 Reporting Cycles</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Generate weekly payroll reports every Friday</li>
                <li>• Review monthly summaries for trends</li>
                <li>• Export Sage files before payroll deadlines</li>
                <li>• Archive historical data regularly</li>
                <li>• Cross-reference with actual payroll outputs</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔍 Data Accuracy</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Verify clock-in/out times match physical records</li>
                <li>• Confirm break times are accurately recorded</li>
                <li>• Check SSP calculations against government guidelines</li>
                <li>• Validate holiday pay against contract terms</li>
                <li>• Review late arrival flags for disciplinary actions</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use horizontal scrolling for wide data tables</li>
                <li>• Filter by specific staff members for focused reviews</li>
                <li>• Export CSV files directly to mobile devices</li>
                <li>• Review summaries on tablets for better readability</li>
                <li>• Save filtered views for quick access later</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⚡ Efficiency Tips</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Set up recurring date ranges for regular reporting</li>
                <li>• Use staff filtering to review individual performance</li>
                <li>• Export data early to allow time for payroll processing</li>
                <li>• Review break applications to ensure policy compliance</li>
                <li>• Monitor SSP usage for absence management insights</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔒 Compliance</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Ensure SSP calculations meet legal requirements</li>
                <li>• Verify holiday pay matches employment contracts</li>
                <li>• Maintain accurate working time records</li>
                <li>• Protect employee data privacy in exports</li>
                <li>• Keep audit trails for payroll compliance</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Advanced Analytics</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Track overtime patterns and costs</li>
                <li>• Monitor break compliance across teams</li>
                <li>• Analyze absence trends by department</li>
                <li>• Compare budgeted vs actual hours worked</li>
                <li>• Identify productivity patterns and peaks</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}